<script setup lang="ts">
import { computed, shallowRef, watch } from 'vue'
import AppErrorState from '@/components/app-error-state'
import LoginPrompt from '@/components/login-prompt'
import MessageHistoryCard from '@/components/message-history-card'
import MessageEmptyState from '@/components/message-empty-state'
import { useElementVisibility } from '@/composables/useElementVisibility'
import { useMessageHistory, type MessageHistoryEntry } from '@/composables/useMessageHistory'
import { useRouteRefreshSignal } from '@/composables/useRouteRefreshSignal'
import { getDreamaDomain } from '@/constants/domain'
import { applyDocumentTitle } from '@/utils/page-title'
import { openLegacyPlayerUrl } from '@/utils/legacy-player'
import { getRuntimePerformanceBudget } from '@/utils/performance/runtime-budget'
import { useUserStore } from '@/stores/user'

defineOptions({
  name: 'MessagesView',
})

const runtimeBudget = getRuntimePerformanceBudget()
const userStore = useUserStore()
const loadMoreTarget = shallowRef<HTMLElement | null>(null)
const { loadingInitial, loadingMore, error, totalCount, canLoadMore, refresh, fetchNext, items } =
  useMessageHistory()

const { isVisible: isLoadMoreVisible } = useElementVisibility(loadMoreTarget, {
  rootMargin: runtimeBudget.isConstrained ? '240px 0px' : '420px 0px',
  threshold: 0,
})

const showInitialSkeleton = computed(() => loadingInitial.value && totalCount.value === 0)
const isLoggedIn = computed(() => userStore.isLoggedIn())
const showEmpty = computed(() => !loadingInitial.value && !error.value && items.value.length === 0)
const skeletonCards = Array.from({ length: 12 }, (_, index) => index)

watch([isLoadMoreVisible, canLoadMore], ([visible, allowed]) => {
  if (visible && allowed) {
    void fetchNext()
  }
})

useRouteRefreshSignal('messages', async () => {
  if (!isLoggedIn.value) {
    return
  }

  await refresh()
})

function openMessage(item: MessageHistoryEntry) {
  if (!item.pipeId) {
    return
  }

  const url = new URL('/view.html', getDreamaDomain())
  url.searchParams.set('pipe_id', item.pipeId)
  openLegacyPlayerUrl(url.toString())
}

applyDocumentTitle('消息')

watch(
  isLoggedIn,
  (loggedIn) => {
    if (loggedIn) {
      void refresh()
    }
  },
  { immediate: true },
)
</script>

<template>
  <section class="messages-page">
    <div class="messages-page__inner">
      <LoginPrompt v-if="!isLoggedIn" message="登录后查看消息" />

      <div v-else-if="showInitialSkeleton" class="messages-page__grid" aria-label="消息加载中">
        <article v-for="item in skeletonCards" :key="item" class="messages-page__skeleton-card">
          <div class="messages-page__skeleton-cover" />
        </article>
      </div>

      <AppErrorState v-else-if="error" variant="full" @retry="refresh" />

      <MessageEmptyState v-else-if="showEmpty" />

      <div v-else class="messages-page__grid">
        <MessageHistoryCard
          v-for="item in items"
          :key="item.id"
          :item="item"
          @select="openMessage"
        />
      </div>

      <div
        v-if="totalCount > 0"
        ref="loadMoreTarget"
        class="messages-page__load-more"
        aria-hidden="true"
      >
        <span v-if="loadingMore">正在加载更早的消息...</span>
      </div>
    </div>
  </section>
</template>

<style scoped>
.messages-page {
  min-width: 0;
}

.messages-page__inner {
  display: flex;
  min-height: calc(100vh - 16px);
  width: 100%;
  flex-direction: column;
  gap: 18px;
}

.messages-page__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 2px;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  overflow: hidden;
  border-radius: 16px;
}

.messages-page__skeleton-card {
  display: flex;
  flex-direction: column;
  aspect-ratio: 244 / 354.909;
  padding: 12px;
  background: transparent;
}

.messages-page__skeleton-cover {
  position: relative;
  overflow: hidden;
  background: var(--app-skeleton-line);
}

.messages-page__skeleton-cover::after {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent, var(--app-skeleton-shimmer), transparent);
  animation: message-skeleton 1.5s linear infinite;
  content: '';
}

.messages-page__skeleton-cover {
  height: 100%;
  border-radius: 12px;
}

.messages-page__load-more {
  display: flex;
  min-height: 56px;
  align-items: center;
  justify-content: center;
  color: var(--app-text-soft);
  font-size: 13px;
}

@keyframes message-skeleton {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(100%);
  }
}

@media (max-width: 720px) {
  .messages-page__grid {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  }
}
</style>
